import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box,
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Divider,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import { Visibility, VisibilityOff, Save, Edit, Logout, Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getEnabledCountries } from '../../services/configService';
import { NotificationPermissionDialog } from '../Notifications';
import AccountPageShell from '../Account/AccountPageShell';
import { COLORS } from '../../theme/tokens';
import { FORM_FIELD_SX, PANEL_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';
// Default countries list as fallback
const defaultCountries = [
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'EE', name: 'Estonia' },
  { code: 'US', name: 'United States' },
  { code: 'XK', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' }
];

const Profile = () => {
  const { t } = useTranslation();
  const { user, loading, error, updateProfile, logout, isAuthenticated } = useContext(AuthContext);
  const [editing, setEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState(defaultCountries);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() => {
    // Check if user explicitly disabled notifications
    const isDisabled = localStorage.getItem('notifications_disabled') === 'true';
    if (isDisabled) return 'disabled';
    return Notification?.permission || 'default';
  });
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    password: '',
    phonenumber: '',
    address: '',
    city: '',
    postcode: '',
    country_id: '',
    company: '',
    company_no: '',
    company_tax_no: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();
  // Removed unused countryCodeToId map
  
  // Fetch countries from database
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const response = await getEnabledCountries();
        if (response && response.data) {
          setCountries(response.data);
        } else {
          // Keep using default countries list (already set in initial state)
        }
      } catch (error) {
        // Keep using default countries list (already set in initial state)
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    // Redirect if not authenticated
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
    // Initialize form data with user data
    if (user) {
      // For debugging
      
      
      setFormData({
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        password: '',
        phonenumber: user.phonenumber || '',
        address: user.address || '',
        city: user.city || '',
        postcode: user.postcode || '',
        country_id: user.country_id || 0, // Use 0 as default for number type consistency
        company: user.company || '',
        company_no: user.company_no || '',
        company_tax_no: user.company_tax_no || ''
      });
    }
  }, [user, isAuthenticated, loading, navigate]);
  if (loading || !user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear success message when user changes something
    if (success) {
      setSuccess(false);
    }
    // Clear field-specific error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: null });
    }
  };
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const validateForm = () => {
    const errors = {};
    if (formData.firstname.trim() === '') {
      errors.firstname = t('account.errFirstName');
    }
    if (formData.lastname.trim() === '') {
      errors.lastname = t('account.errLastName');
    }
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Only include password if it's provided
    const updateData = {
      firstname: formData.firstname,
      lastname: formData.lastname,
      phonenumber: formData.phonenumber,
      address: formData.address,
      city: formData.city,
      postcode: formData.postcode,
      country_id: formData.country_id,
      company: formData.company,
      company_no: formData.company_no,
      company_tax_no: formData.company_tax_no
    };
    if (formData.password.trim() !== '') {
      updateData.password = formData.password;
    }
    setSaving(true);
    const updated = await updateProfile(updateData);
    setSaving(false);
    if (updated) {
      setEditing(false);
      setSuccess(true);
      setFormData({
        ...formData,
        password: '' // Clear password field after successful update
      });
    }
  };
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  return (
    <AccountPageShell title="Account details" maxWidth="xl">
      <Box sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          <Button 
            variant="outlined" 
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={SECONDARY_BUTTON_SX}
          >
            {t('nav.logout')}
          </Button>
        </Box>
        <Divider sx={{ mb: 4 }} />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{t('account.updated')}</Alert>}
        <Box component="form" onSubmit={handleSubmit} sx={FORM_FIELD_SX}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {t('account.accountInformation')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('account.email')}
                value={user.email}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                disabled
                helperText={t('account.emailLocked')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('auth.firstName')}
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                fullWidth
                required
                disabled={!editing}
                error={!!formErrors.firstname}
                helperText={formErrors.firstname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('auth.lastName')}
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                fullWidth
                required
                disabled={!editing}
                error={!!formErrors.lastname}
                helperText={formErrors.lastname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('account.phoneNumber')}
                name="phonenumber"
                value={formData.phonenumber}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('account.address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('checkout.city')}
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('checkout.postalCode')}
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {loadingCountries ? (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '56px' }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2">{t('account.loadingCountries')}</Typography>
                </Box>
              ) : (
                <Autocomplete
                  id="country-select"
                  options={countries}
                  disabled={!editing}
                  getOptionLabel={(option) => option.name || ''} // Add fallback for safety
                  // Directly compare the country object's id with the formData.country_id
                  value={countries.find(country => country.id === formData.country_id) || null}
                  isOptionEqualToValue={(option, value) => option.id === value.id} // Ensure correct comparison
                  onChange={(event, newValue) => {
                    // Directly use the id from the selected country object
                    const countryId = newValue ? newValue.id : null;
                    
                    setFormData({
                      ...formData,
                      country_id: countryId // Set the numeric ID
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('checkout.country')}
                      disabled={!editing}
                      fullWidth
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('checkout.companyOptional')}
                name="company"
                value={formData.company}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('checkout.companyRegOptional')}
                name="company_no"
                value={formData.company_no}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('checkout.companyTaxOptional')}
                name="company_tax_no"
                value={formData.company_tax_no}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <TextField
                  label={t('account.newPassword')}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                />
              </Grid>
            )}

            {/* Notification Settings Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                {t('account.notificationSettings')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ ...PANEL_SX, p: 3, backgroundColor: COLORS.surfacePanel }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <NotificationsIcon color={notificationPermission === 'granted' ? 'primary' : 'action'} />
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {t('account.pushNotifications')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {notificationPermission === 'granted' && '✅ Enabled - You\'ll receive order updates and special offers'}
                        {notificationPermission === 'denied' && '❌ Blocked - Enable in your browser settings to receive notifications'}
                        {notificationPermission === 'disabled' && '🔕 Disabled - You won\'t receive notifications'}
                        {notificationPermission === 'default' && '🔔 Not enabled - Get updates about your orders and exclusive offers'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    {(notificationPermission === 'default' || notificationPermission === 'disabled') && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<NotificationsIcon />}
                        onClick={() => setShowNotificationDialog(true)}
                      >
                        {t('account.enable')}
                      </Button>
                    )}
                    {notificationPermission === 'granted' && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowNotificationDialog(true)}
                      >
                        {t('account.manage')}
                      </Button>
                    )}
                    {notificationPermission === 'denied' && (
                      <Button
                        variant="outlined"
                        disabled
                        size="small"
                      >
                        {t('account.blocked')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            {!editing ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                sx={PRIMARY_BUTTON_SX}
              >
                {t('account.editProfile')}
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  sx={{ ...SECONDARY_BUTTON_SX, mr: 2 }}
                  onClick={() => {
                    setEditing(false);
                    // Reset form data to user data
                    if (user) {
                      setFormData({
                        firstname: user.firstname || '',
                        lastname: user.lastname || '',
                        password: '',
                        phonenumber: user.phonenumber || '',
                        address: user.address || '',
                        city: user.city || '',
                        postcode: user.postcode || '',
                        country_id: user.country_id || 0, // Use 0 as default for number type consistency
                        company: user.company || '',
                        company_no: user.company_no || '',
                        company_tax_no: user.company_tax_no || ''
                      });
                    }
                    setFormErrors({});
                  }}
                >
                  {t('account.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving}
                  sx={PRIMARY_BUTTON_SX}
                >
                  {saving ? <CircularProgress size={24} /> : t('account.saveChanges')}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Notification Permission Dialog */}
      <NotificationPermissionDialog
        open={showNotificationDialog}
        onClose={() => {
          setShowNotificationDialog(false);
          // Update permission status after dialog closes
          const isDisabled = localStorage.getItem('notifications_disabled') === 'true';
          if (isDisabled) {
            setNotificationPermission('disabled');
          } else {
            setNotificationPermission(Notification?.permission || 'default');
          }
        }}
        user={user}
      />
    </AccountPageShell>
  );
};
export default Profile;
